import React from 'react'
import styles from './QrCameraScannerPanel.module.css'

export type QrCameraScannerPanelVariant = 'staff' | 'public'

export interface QrCameraScannerPanelProps {
  variant: QrCameraScannerPanelVariant
  videoRef: React.RefObject<HTMLVideoElement | null>
  statusId: string
  videoAriaLabel: string
  scannerStatus: string
  scannerSuccess: string | null
  scannerError: string | null
  torchSupported: boolean
  torchOn: boolean
  toggleTorch: () => Promise<void>
  flipCameraSupported: boolean
  flipCamera: () => void
  manualScanInput: string
  onManualScanInputChange: (value: string) => void
  onManualSubmit: () => void
  recentScans: string[]
  manualEntryVariant: 'always-visible' | 'collapsible'
  showManualEntry?: boolean
  onRevealManualEntry?: () => void
  hint?: React.ReactNode
  children?: React.ReactNode
}

export const QrCameraScannerPanel: React.FC<QrCameraScannerPanelProps> = ({
  variant,
  videoRef,
  statusId,
  videoAriaLabel,
  scannerStatus,
  scannerSuccess,
  scannerError,
  torchSupported,
  torchOn,
  toggleTorch,
  flipCameraSupported,
  flipCamera,
  manualScanInput,
  onManualScanInputChange,
  onManualSubmit,
  recentScans,
  manualEntryVariant,
  showManualEntry = true,
  onRevealManualEntry,
  hint,
  children,
}) => {
  const variantAttr = variant === 'staff' ? 'staff' : 'public'

  return (
    <div className={styles.root} data-variant={variantAttr}>
      <div className={styles.videoWrap}>
        <video
          ref={videoRef}
          className={styles.video}
          autoPlay
          muted
          playsInline
          aria-label={videoAriaLabel}
          aria-describedby={statusId}
        />
      </div>

      {(torchSupported || flipCameraSupported) && (
        <div className={styles.cameraTools}>
          {torchSupported && (
            <button type="button" className={styles.toolBtn} onClick={() => void toggleTorch()}>
              {torchOn ? 'Torch off' : 'Torch on'}
            </button>
          )}
          {flipCameraSupported && (
            <button type="button" className={styles.toolBtn} onClick={flipCamera}>
              Flip camera
            </button>
          )}
        </div>
      )}

      <p
        id={statusId}
        className={styles.status}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {scannerStatus}
      </p>

      {hint ? <div className={styles.hint}>{hint}</div> : null}

      {scannerSuccess ? <div className={styles.success}>{scannerSuccess}</div> : null}
      {scannerError ? <div className={styles.error}>{scannerError}</div> : null}

      {manualEntryVariant === 'collapsible' && !showManualEntry && onRevealManualEntry ? (
        <button type="button" className={styles.manualRevealBtn} onClick={onRevealManualEntry}>
          Can&apos;t scan? Paste URL or product ID
        </button>
      ) : null}

      {(manualEntryVariant === 'always-visible' || showManualEntry) && (
        <div className={styles.manualRow}>
          <input
            type="text"
            value={manualScanInput}
            onChange={(e) => onManualScanInputChange(e.target.value)}
            placeholder="Paste scan URL or product ID"
            className={styles.manualInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onManualSubmit()
            }}
          />
          <button type="button" className={styles.manualBtn} onClick={onManualSubmit}>
            Add
          </button>
        </div>
      )}

      {recentScans.length > 0 && (
        <div className={styles.recent}>
          <span className={styles.recentLabel}>Recent scans</span>
          <div className={styles.recentList}>
            {recentScans.map((id) => (
              <span key={id} className={styles.recentItem}>{id.slice(0, 8).toUpperCase()}</span>
            ))}
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
